import { lazy, Component, Fragment, Suspense } from 'react';
import styled, { css } from 'styled-components/macro';
import {
  arrayOf,
  shape,
  string,
  func,
  oneOfType,
  number,
  bool,
} from 'prop-types';
import randomHash from 'helpers/generateRandomHash';
import stringToSlug from 'helpers/stringToSlug';

import { socialIcons } from 'components/data-containers/SocialFollowers/constants';

const TippyToolTip = lazy(() => import('../TippyToolTip/TippyToolTip'));

export const Fieldset = styled('div')`
  border: none;
  padding: 0;
  width: 100%;
  display: flex;
  ${({ isRemovedMarginLeft }) =>
    isRemovedMarginLeft ? 'margin-left: 0 !important;' : ''}
  ${({ isSmallScreen }) =>
    isSmallScreen
      ? 'height: 48px; label {font-size: 14px;}'
      : 'flex-flow: row wrap;'}
  align-items: stretch;

  ${({ flexFlow }) => (flexFlow ? `flex-flow: ${flexFlow};` : '')}

  > span {
    display: flex;
    flex: 1 1 auto;
  }

  @media screen and (min-width: 30em) {
    width: auto;
  }
`;

export const Label = styled('label')`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 auto;
  text-align: center;
  background: ${({ theme }) => theme.Sand};
  color: ${({ theme }) => theme.NightBlue};
  font-weight: 600;
  padding: ${({ theme }) => `${theme.spacing.small} ${theme.spacing.large}`};
  min-width: 5em;
  transition: ${({ theme }) =>
    `${theme.transitionDuration} ${theme.timingFunction} background`};

  ${({ isButtonNewDesigned }) =>
    isButtonNewDesigned &&
    css`
      background: ${({ theme }) => theme.White};
      color: ${({ theme }) => theme.AccentGreen};
      border: 1px solid ${({ theme }) => theme.AccentGreen};

      &:first-of-type {
        border-top-left-radius: 3px;
        border-bottom-left-radius: 3px;
      }
      &:last-of-type {
        border-top-right-radius: 3px;
        border-bottom-right-radius: 3px;
        border-right: 1px solid ${({ theme }) => theme.AccentGreen};
      }
    `}

  @media screen and (min-width: 30em) {
    white-space: nowrap;
  }
`;

export const Radio = styled('input')`
  position: absolute;
  clip: rect(0, 0, 0, 0);

  &:checked + label {
    color: white;
    background: ${({ theme }) => theme.AccentGreen};
  }
  &:hover:not(:checked) + label {
    background: ${({ theme }) => theme.LightGrey};
  }

  &:active:not(:checked) + label {
    background: ${({ theme }) => theme.Grey};
  }

  &:disabled:not(:checked) + label {
    background: ${({ theme }) => theme.LightGrey};
    color: ${({ theme }) => theme.CasperGrey};
    cursor: not-allowed;
  }

  ${({ isButtonNewDesigned }) =>
    isButtonNewDesigned &&
    css`
      &:checked + label {
        color: white;
        background: ${({ theme }) => theme.AccentGreen};
      }
      &:hover:not(:checked) + label {
        background: #daf9f2; // 15% opacity
        ${({ isSearchTabs }) =>
          !isSearchTabs && 'border: 1px solid #85ebd3; // 50% opacity'}
      }
      &:active:not(:checked) + label {
        background: #daf9f2; // 15% opacity
        border: 1px solid #85ebd3;
      }
      &:disabled:not(:checked) + label {
        background: ${({ theme }) => theme.LightGrey};
        color: #8ea6be;
        cursor: not-allowed;
        border-color: ${({ theme }) => theme.LightGrey};
        opacity: 0.5;

        span {
          transition: none;
        }
      }
    `};
`;

function RadioItem(props) {
  const {
    item,
    selectedOption,
    onChange,
    name,
    allDisabled,
    id,
    hasOnlyIconOption,
    isButtonNewDesigned = false,
    className,
    isSearchTabs,
  } = props;
  const { label, value, disabled } = item;
  const itemIconName = label.split(' ')[0]?.toLowerCase();
  const Icon = label
    ? selectedOption === value
      ? socialIcons[itemIconName]?.selected
      : socialIcons[itemIconName]?.main
    : null;

  return (
    <Fragment key={value}>
      <Radio
        type="radio"
        name={name}
        value={value}
        id={id}
        checked={selectedOption === value}
        onChange={() => onChange({ label, value })}
        disabled={allDisabled || disabled}
        isButtonNewDesigned={Icon ? false : isButtonNewDesigned}
        isSearchTabs={isSearchTabs}
      />
      {Icon && hasOnlyIconOption && (
        <Label htmlFor={id} data-pw={`option-${item.value}`}>
          <Icon width="20px" height="20px" />
        </Label>
      )}
      {(!hasOnlyIconOption || value === -1) && (
        <Label
          htmlFor={id}
          isButtonNewDesigned={isButtonNewDesigned}
          data-pw={`option-${item.value}`}
          className={className}
        >
          {label}
        </Label>
      )}
    </Fragment>
  );
}

/** A fieldset with multiple radio buttons with a callback to change state */
class RadioGroup extends Component {
  constructor(props) {
    super(props);
    // Assigns a random Id to the instance of this component so we can re-use the RadioGroup multiple times in one page
    // The random ID will not change per render, only whenever an instance is mounted
    this.randomId = randomHash();
  }

  render() {
    const {
      options,
      name,
      disabled,
      isSmallScreen,
      isRemovedMarginLeft,
      tooltipMaxWidth,
      flexFlow,
      ...rest
    } = this.props;

    return (
      <Fieldset
        data-pw={`${stringToSlug(name)}-radio-group`}
        data-gtag="radio-group"
        isSmallScreen={isSmallScreen}
        isRemovedMarginLeft={isRemovedMarginLeft}
        flexFlow={flexFlow}
      >
        {options.map((item, index) => {
          const id = `${this.randomId}_${item.value}`;
          const last = index === options.length - 1 ? 'last' : '';

          return item.tooltip ? (
            <Suspense fallback={null} key={id}>
              <TippyToolTip
                delay={[0, 0]}
                tooltipContent={item.tooltip}
                enabled={!disabled}
                maxWidth={tooltipMaxWidth}
                className={last}
              >
                <RadioItem
                  item={item}
                  id={id}
                  {...rest}
                  allDisabled={disabled}
                />
              </TippyToolTip>
            </Suspense>
          ) : (
            <RadioItem
              key={id}
              item={item}
              id={id}
              {...rest}
              allDisabled={disabled}
              className={last}
            />
          );
        })}
      </Fieldset>
    );
  }
}

RadioGroup.propTypes = {
  disabled: bool,
  /** The possible options the user can select with additional properties
   * for internal management.
   */
  options: arrayOf(
    shape({
      label: string.isRequired,
      value: oneOfType([string, number]).isRequired,
    })
  ).isRequired,
  /** The name of the options */
  name: string.isRequired,
  /** selectedOption is the button that is enabled by default */
  selectedOption: oneOfType([string, number]).isRequired,
  /** The callback to trigger on a input change */
  onChange: func.isRequired,
  isSmallScreen: bool,
  isRemovedMarginLeft: bool,
  tooltipMaxWidth: string,
  flexFlow: string,
  hasOnlyIconOption: bool,
  isButtonNewDesigned: bool,
};

export default RadioGroup;
